import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Package,
  Filter,
  Pencil,
  Copy,
  Trash2,
  Eye,
  Download,
  EyeOff,
  Eye as EyeOn,
  X,
  Loader2,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { usePreferredCurrencyOptional } from '../context/CurrencySettingsContext';
import { useDashboardTheme } from '../context/DashboardThemeContext';
import { supabase } from '../lib/supabase';
import { cn } from '@/lib/utils';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';

type ProductRow = {
  id: string;
  name: string;
  slug: string;
  price_xof: number | null;
  price_eur: number | null;
  is_active: boolean;
  created_at: string;
  category_id: string | null;
  collection_id: string | null;
  product_images: { url: string; position: number; is_featured: boolean }[];
  product_variants?: { id: string; inventory: { quantity: number }[] | { quantity: number } }[];
};

type Category = { id: string; name: string; slug: string };
type Collection = { id: string; name: string; slug: string };

export function DashboardProducts() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { theme } = useDashboardTheme();
  const currencyContext = usePreferredCurrencyOptional();
  const preferredCurrency = currencyContext?.preferredCurrency ?? 'XOF';
  const isLight = theme === 'light';

  const [products, setProducts] = useState<ProductRow[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<{ status: 'all' | 'active' | 'inactive'; categoryId: string; collectionId: string }>({
    status: 'all',
    categoryId: '',
    collectionId: '',
  });
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [previewProduct, setPreviewProduct] = useState<{ id: string; name: string } | null>(null);
  const [previewImages, setPreviewImages] = useState<{ url: string; alt_text: string | null }[]>([]);
  const [previewVariants, setPreviewVariants] = useState<{ id: string; title: string; image_url: string | null }[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [previewVariantId, setPreviewVariantId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadProducts = useCallback(() => {
    setLoading(true);
    let q = supabase
      .from('products')
      .select(
        `
        id,
        name,
        slug,
        price_xof,
        price_eur,
        is_active,
        created_at,
        category_id,
        collection_id,
        product_images(url, position, is_featured),
        product_variants(id, inventory(quantity))
      `
      )
      .order('created_at', { ascending: false });

    if (filters.status === 'active') q = q.eq('is_active', true);
    if (filters.status === 'inactive') q = q.eq('is_active', false);
    if (filters.categoryId) q = q.eq('category_id', filters.categoryId);
    if (filters.collectionId) q = q.eq('collection_id', filters.collectionId);

    q.then(({ data }) => {
      setProducts((data ?? []) as ProductRow[]);
      setLoading(false);
    });
  }, [filters.status, filters.categoryId, filters.collectionId]);

  const getProductTotalStock = (p: ProductRow): number => {
    const variants = p.product_variants ?? [];
    return variants.reduce((sum, v) => {
      const inv = v.inventory;
      const q = Array.isArray(inv) ? inv[0]?.quantity : (inv as { quantity?: number } | undefined)?.quantity;
      return sum + (q ?? 0);
    }, 0);
  };

  const filteredProducts = searchQuery.trim()
    ? products.filter(
        (p) =>
          p.name.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
          (p.slug && p.slug.toLowerCase().includes(searchQuery.trim().toLowerCase()))
      )
    : products;

  const handleToggleActive = async (productId: string, current: boolean) => {
    setActionLoading(productId);
    const { error } = await supabase.from('products').update({ is_active: !current, updated_at: new Date().toISOString() }).eq('id', productId);
    if (!error) loadProducts();
    setActionLoading(null);
  };

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (!previewProduct) {
      setPreviewImages([]);
      setPreviewVariants([]);
      setPreviewVariantId(null);
      setPreviewImageIndex(0);
      return;
    }
    setPreviewLoading(true);
    Promise.all([
      supabase.from('product_images').select('url, alt_text').eq('product_id', previewProduct.id).order('position'),
      supabase.from('product_variants').select('id, title, image_url').eq('product_id', previewProduct.id),
    ]).then(([imgRes, varRes]) => {
      setPreviewImages((imgRes.data ?? []) as { url: string; alt_text: string | null }[]);
      setPreviewVariants((varRes.data ?? []) as { id: string; title: string; image_url: string | null }[]);
      setPreviewVariantId(null);
      setPreviewImageIndex(0);
      setPreviewLoading(false);
    });
  }, [previewProduct?.id]);

  useEffect(() => {
    Promise.all([
      supabase.from('categories').select('id, name, slug').order('name'),
      supabase.from('collections').select('id, name, slug').order('name'),
    ]).then(([catRes, colRes]) => {
      setCategories((catRes.data ?? []) as Category[]);
      setCollections((colRes.data ?? []) as Collection[]);
    });
  }, []);

  const applyFilters = () => {
    setFilterOpen(false);
    loadProducts();
  };

  const handleDelete = async (id: string) => {
    setActionLoading(id);
    await supabase.from('products').delete().eq('id', id);
    setDeleteConfirm(null);
    setActionLoading(null);
    loadProducts();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setActionLoading('bulk-delete');
    for (const id of selectedIds) {
      await supabase.from('products').delete().eq('id', id);
    }
    setBulkDeleteConfirm(false);
    setSelectedIds(new Set());
    setActionLoading(null);
    loadProducts();
  };

  const handleBulkVisibility = async (active: boolean) => {
    if (selectedIds.size === 0) return;
    setActionLoading('bulk-visibility');
    await supabase.from('products').update({ is_active: active }).in('id', [...selectedIds]);
    setSelectedIds(new Set());
    setActionLoading(null);
    loadProducts();
  };

  const handleDuplicate = async (product: ProductRow) => {
    setActionLoading(product.id);
    const { data: fullProduct, error: fetchErr } = await supabase.from('products').select('*').eq('id', product.id).single();
    if (fetchErr || !fullProduct) {
      setActionLoading(null);
      return;
    }
    const p = fullProduct as Record<string, unknown>;
    const { data: newProduct, error: productError } = await supabase
      .from('products')
      .insert({
        name: `${product.name} (copie)`,
        slug: `${product.slug}-copie-${Date.now()}`,
        description: p.description ?? null,
        description_html: p.description_html ?? null,
        category_id: p.category_id ?? null,
        collection_id: p.collection_id ?? null,
        base_price: p.base_price ?? 0,
        price_xof: p.price_xof ?? null,
        price_eur: p.price_eur ?? null,
        price_usd: p.price_usd ?? null,
        compare_at_price: p.compare_at_price ?? null,
        compare_at_price_xof: p.compare_at_price_xof ?? null,
        compare_at_price_eur: p.compare_at_price_eur ?? null,
        compare_at_price_usd: p.compare_at_price_usd ?? null,
        currency: p.currency ?? 'XOF',
        sku: p.sku ? `${p.sku}-copy` : null,
        tags: p.tags ?? [],
        is_active: false,
        is_featured: false,
        is_available_for_sale: p.is_available_for_sale ?? true,
        requires_customization: p.requires_customization ?? false,
        meta_title: p.meta_title ?? null,
        meta_description: p.meta_description ?? null,
        weight_grams: p.weight_grams ?? null,
      })
      .select('id')
      .single();

    if (productError || !newProduct) {
      setActionLoading(null);
      return;
    }

    const [images, shots, options] = await Promise.all([
      supabase.from('product_images').select('*').eq('product_id', product.id),
      supabase.from('product_shots').select('*').eq('product_id', product.id),
      supabase.from('product_options').select('*').eq('product_id', product.id),
    ]);

    const optIdMap: Record<string, string> = {};
    for (const opt of options.data ?? []) {
      const { data: newOpt } = await supabase.from('product_options').insert({
        product_id: newProduct.id,
        name: opt.name,
        position: opt.position,
      }).select('id').single();
      if (newOpt) optIdMap[opt.id] = newOpt.id;
    }

    for (const opt of options.data ?? []) {
      const vals = await supabase.from('product_option_values').select('*').eq('option_id', opt.id);
      for (const v of vals.data ?? []) {
        if (optIdMap[opt.id]) {
          await supabase.from('product_option_values').insert({
            option_id: optIdMap[opt.id],
            value: v.value,
            position: v.position,
          });
        }
      }
    }

    for (const img of images.data ?? []) {
      await supabase.from('product_images').insert({
        product_id: newProduct.id,
        url: img.url,
        alt_text: img.alt_text,
        position: img.position,
        is_featured: img.is_featured,
      });
    }
    for (const shot of shots.data ?? []) {
      await supabase.from('product_shots').insert({
        product_id: newProduct.id,
        url: shot.url,
        alt_text: shot.alt_text,
        caption: shot.caption,
        model_info: shot.model_info,
        position: shot.position,
      });
    }

    const variants = await supabase.from('product_variants').select('*').eq('product_id', product.id);
    for (const v of variants.data ?? []) {
      const { data: newV } = await supabase.from('product_variants').insert({
        product_id: newProduct.id,
        title: v.title,
        sku: v.sku ? `${v.sku}-copy` : null,
        price: v.price,
        price_xof: v.price_xof,
        price_eur: v.price_eur,
        price_usd: v.price_usd,
        compare_at_price: v.compare_at_price,
        compare_at_price_xof: v.compare_at_price_xof,
        compare_at_price_eur: v.compare_at_price_eur,
        compare_at_price_usd: v.compare_at_price_usd,
        available_for_sale: v.available_for_sale,
        selected_options: v.selected_options,
        image_url: v.image_url,
      }).select('id').single();
      if (newV) {
        const inv = await supabase.from('inventory').select('*').eq('variant_id', v.id).single();
        if (inv.data) {
          await supabase.from('inventory').insert({
            variant_id: newV.id,
            quantity: inv.data.quantity,
            low_stock_threshold: inv.data.low_stock_threshold,
          });
        }
      }
    }

    setActionLoading(null);
    loadProducts();
    navigate(`/espace-business/products/edit/${newProduct.id}`);
  };

  const exportSelected = () => {
    const toExport = selectedIds.size ? products.filter((p) => selectedIds.has(p.id)) : products;
    const headers = ['Nom', 'Slug', 'Prix XOF', 'Prix EUR', 'Statut'];
    const rows = toExport.map((p) => [
      p.name,
      p.slug,
      p.price_xof ?? '',
      p.price_eur ?? '',
      p.is_active ? 'Actif' : 'Inactif',
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `produits-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const borderCls = isLight ? 'border-border/60' : 'border-white/10';
  const cardCls = isLight ? 'border-border/80 bg-card' : 'border-white/10 bg-white/5';

  return (
    <div className="flex flex-1 flex-col gap-6">
      <div className={cn('rounded-3xl border p-6 md:p-8', cardCls)}>
        <p className="flex items-center gap-2 font-['Rubik'] text-sm font-medium text-white/60">
          <Package className="size-4" />
          {t('business.products')}
        </p>
        <h2 className="mt-1 font-['Sora'] text-2xl font-semibold tracking-tight text-white md:text-3xl">
          Catalogue
        </h2>
        <p className="mt-1 font-['Rubik'] text-sm text-white/50">
          Gérer les produits de la boutique.
        </p>
      </div>

      {/* Barre d'actions groupées */}
      {selectedIds.size > 0 && (
        <div className={cn('flex flex-wrap items-center gap-3 rounded-2xl border p-4', borderCls, isLight ? 'bg-muted/30' : 'bg-white/5')}>
          <span className="font-['Rubik'] text-sm text-foreground">
            {selectedIds.size} produit(s) sélectionné(s)
          </span>
          <Button variant="outline" size="sm" className="gap-2 rounded-xl" onClick={exportSelected}>
            <Download className="size-4" />
            Exporter
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 rounded-xl"
            onClick={() => handleBulkVisibility(true)}
            disabled={!!actionLoading}
          >
            <EyeOn className="size-4" />
            Afficher
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 rounded-xl"
            onClick={() => handleBulkVisibility(false)}
            disabled={!!actionLoading}
          >
            <EyeOff className="size-4" />
            Masquer
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 rounded-xl text-red-400 hover:text-red-300"
            onClick={() => setBulkDeleteConfirm(true)}
            disabled={!!actionLoading}
          >
            <Trash2 className="size-4" />
            Supprimer
          </Button>
          <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => setSelectedIds(new Set())}>
            Annuler
          </Button>
        </div>
      )}

      <Card className={cn('overflow-hidden rounded-2xl', cardCls)}>
        <CardHeader className={cn('border-b', borderCls)}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <CardTitle>Liste des produits</CardTitle>
              <CardDescription>{filteredProducts.length} produit(s){searchQuery.trim() ? ` sur ${products.length}` : ''}</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  type="search"
                  placeholder="Rechercher un produit…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn('w-64 pl-9 rounded-xl', isLight ? 'bg-background' : 'bg-white/5')}
                  aria-label="Rechercher un produit"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 rounded-xl"
                onClick={() => setFilterOpen(true)}
              >
                <Filter className="size-4" />
                Filtres
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto pt-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <p className="py-10 text-center font-['Rubik'] text-white/50">
              {searchQuery.trim() ? 'Aucun produit ne correspond à la recherche.' : 'Aucun produit.'}
            </p>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className={cn('border-b', borderCls)}>
                  <th className="w-12 pb-3 pr-2">
                    <Checkbox
                      checked={selectedIds.size === filteredProducts.length && filteredProducts.length > 0}
                      onCheckedChange={(checked) => (checked ? setSelectedIds(new Set(filteredProducts.map((p) => p.id))) : setSelectedIds(new Set()))}
                      aria-label="Tout sélectionner"
                    />
                  </th>
                  <th className="w-28 pb-3 pr-2 font-['Rubik'] text-xs uppercase tracking-wider text-white/50">Image</th>
                  <th className="pb-3 pr-4 font-['Rubik'] text-xs uppercase tracking-wider text-white/50">Produit</th>
                  <th className="pb-3 px-4 font-['Rubik'] text-xs uppercase tracking-wider text-white/50">
                    Prix ({preferredCurrency})
                  </th>
                  <th className="pb-3 px-4 font-['Rubik'] text-xs uppercase tracking-wider text-white/50">Stock</th>
                  <th className="pb-3 px-4 font-['Rubik'] text-xs uppercase tracking-wider text-white/50">Statut</th>
                  <th className="pb-3 px-4 font-['Rubik'] text-xs uppercase tracking-wider text-white/50">Activer</th>
                  <th className="pb-3 pl-4 font-['Rubik'] text-xs uppercase tracking-wider text-white/50">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((p) => {
                  const images = p.product_images ?? [];
                  const thumb = images.find((i: any) => i.is_featured) ?? images[0];
                  return (
                    <tr key={p.id} className={cn('border-b border-white/5')}>
                      <td className="w-12 py-4 pr-2">
                        <Checkbox
                          checked={selectedIds.has(p.id)}
                          onCheckedChange={(checked) => setSelectedIds((prev) => { const next = new Set(prev); if (checked) next.add(p.id); else next.delete(p.id); return next; })}
                          aria-label={`Sélectionner ${p.name}`}
                        />
                      </td>
                      <td className="w-28 py-4 pr-2">
                        {thumb?.url ? (
                          <div className="relative group inline-block">
                            <img
                              src={thumb.url}
                              alt={p.name}
                              className="h-20 w-20 rounded-xl object-cover border border-white/10 bg-white/5"
                            />
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPreviewProduct({ id: p.id, name: p.name }); }}
                              className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity text-white"
                              title="Agrandir / Aperçu"
                              aria-label="Aperçu image"
                            >
                              <Maximize2 className="size-5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-white/10 bg-white/5">
                            <Package className="size-5 text-white/30" />
                          </div>
                        )}
                      </td>
                      <td className="py-4 pr-4 font-['Rubik'] text-white">{p.name}</td>
                      <td className="py-4 px-4 font-['Rubik'] text-white/80">
                        {preferredCurrency === 'EUR'
                          ? p.price_eur != null
                            ? `${Number(p.price_eur).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                            : '—'
                          : p.price_xof != null
                            ? `${Number(p.price_xof).toLocaleString()} XOF`
                            : '—'}
                      </td>
                      <td className="py-4 px-4 font-['Rubik'] text-white/80 tabular-nums">
                        {(() => {
                          const stock = getProductTotalStock(p);
                          return stock >= 0 ? `${stock} / ${stock}` : '—';
                        })()}
                      </td>
                      <td className="py-4 px-4">
                        <Badge variant={p.is_active ? 'default' : 'secondary'} className={cn(p.is_active && 'bg-emerald-600 hover:bg-emerald-600/90')}>
                          {p.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        <Switch
                          checked={p.is_active}
                          onCheckedChange={() => handleToggleActive(p.id, p.is_active)}
                          disabled={actionLoading === p.id}
                          aria-label={p.is_active ? 'Désactiver le produit' : 'Activer le produit'}
                        />
                      </td>
                      <td className="py-4 pl-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/espace-business/products/edit/${p.id}`}
                            className="inline-flex items-center gap-1 rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white"
                            title="Modifier"
                          >
                            <Pencil className="size-4" />
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleDuplicate(p)}
                            disabled={actionLoading === p.id}
                            className="inline-flex items-center gap-1 rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white disabled:opacity-50"
                            title="Dupliquer"
                          >
                            {actionLoading === p.id ? <Loader2 className="size-4 animate-spin" /> : <Copy className="size-4" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm({ id: p.id, name: p.name })}
                            disabled={!!actionLoading}
                            className="inline-flex items-center gap-1 rounded-lg p-2 text-white/60 hover:bg-red-500/20 hover:text-red-400 disabled:opacity-50"
                            title="Supprimer"
                          >
                            <Trash2 className="size-4" />
                          </button>
                          <Link
                            to={`/product/${p.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white"
                            title="Voir sur le site"
                          >
                            <Eye className="size-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Modal Filtres */}
      {filterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setFilterOpen(false)}>
          <div
            className={cn('w-full max-w-md rounded-2xl border p-6 shadow-xl', borderCls, isLight ? 'bg-card' : 'bg-neutral-900')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Sora'] text-lg font-semibold text-foreground">Filtres</h3>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="rounded-lg p-2 text-muted-foreground hover:bg-white/10"
                aria-label="Fermer"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-foreground">Statut</Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters((f) => ({ ...f, status: value as 'all' | 'active' | 'inactive' }))}
                >
                  <SelectTrigger className={cn('w-full rounded-xl', borderCls)}>
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="active">Actifs</SelectItem>
                    <SelectItem value="inactive">Inactifs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Catégorie</Label>
                <Select
                  value={filters.categoryId || '__all__'}
                  onValueChange={(value) => setFilters((f) => ({ ...f, categoryId: (value === '__all__' ? '' : value) ?? '' }))}
                >
                  <SelectTrigger className={cn('w-full rounded-xl', borderCls)}>
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Toutes</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Collection</Label>
                <Select
                  value={filters.collectionId || '__all__'}
                  onValueChange={(value) => setFilters((f) => ({ ...f, collectionId: (value === '__all__' ? '' : value) ?? '' }))}
                >
                  <SelectTrigger className={cn('w-full rounded-xl', borderCls)}>
                    <SelectValue placeholder="Collection" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Toutes</SelectItem>
                    {collections.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setFilterOpen(false)}>
                Annuler
              </Button>
              <Button size="sm" className="rounded-xl" onClick={applyFilters}>
                Appliquer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Aperçu produit (galerie + variantes) */}
      {previewProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPreviewProduct(null)}
        >
          <div
            className={cn('flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl border shadow-2xl overflow-hidden', borderCls, isLight ? 'bg-card' : 'bg-neutral-900')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('flex items-center justify-between border-b px-4 py-3', borderCls)}>
              <h3 className="font-['Sora'] text-lg font-semibold text-foreground truncate pr-2">{previewProduct.name}</h3>
              <button
                type="button"
                onClick={() => setPreviewProduct(null)}
                className="rounded-lg p-2 text-muted-foreground hover:bg-white/10"
                aria-label="Fermer"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="flex flex-1 flex-col items-center overflow-auto p-4">
              {previewLoading ? (
                <div className="flex min-h-[280px] items-center justify-center">
                  <Loader2 className="size-10 animate-spin text-muted-foreground" />
                </div>
              ) : (() => {
                const selectedVariant = previewVariantId ? previewVariants.find((v) => v.id === previewVariantId) : null;
                const mainImageUrl = selectedVariant?.image_url || previewImages[previewImageIndex]?.url;
                const hasImages = mainImageUrl || previewImages.length > 0;
                if (!hasImages && !selectedVariant?.image_url) {
                  return (
                    <div className="flex min-h-[280px] items-center justify-center text-muted-foreground">
                      Aucune image pour ce produit.
                    </div>
                  );
                }
                return (
                  <>
                    <div className="relative flex aspect-4/5 w-full max-w-md items-center justify-center overflow-hidden rounded-xl bg-white/5">
                      {mainImageUrl ? (
                        <img
                          src={mainImageUrl}
                          alt={previewProduct.name}
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <Package className="size-16 text-white/30" />
                      )}
                      {previewImages.length > 1 && !selectedVariant?.image_url && (
                        <>
                          <button
                            type="button"
                            onClick={() => setPreviewImageIndex((i) => (i === 0 ? previewImages.length - 1 : i - 1))}
                            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
                            aria-label="Image précédente"
                          >
                            <ChevronLeft className="size-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPreviewImageIndex((i) => (i === previewImages.length - 1 ? 0 : i + 1))}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
                            aria-label="Image suivante"
                          >
                            <ChevronRight className="size-5" />
                          </button>
                        </>
                      )}
                    </div>
                    {previewImages.length > 1 && !selectedVariant && (
                      <ul className="mt-4 flex flex-wrap justify-center gap-2">
                        {previewImages.map((img, idx) => (
                          <li key={img.url + idx}>
                            <button
                              type="button"
                              onClick={() => setPreviewImageIndex(idx)}
                              className={cn(
                                'h-16 w-16 overflow-hidden rounded-lg border-2 transition-colors',
                                idx === previewImageIndex ? 'border-primary ring-2 ring-primary/30' : 'border-white/20 hover:border-white/40'
                              )}
                            >
                              <img src={img.url} alt={img.alt_text ?? ''} className="h-full w-full object-cover" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {previewVariants.length > 0 && (
                      <div className="mt-4 w-full max-w-md space-y-2">
                        <span className="font-['Rubik'] text-sm text-muted-foreground">Variante</span>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setPreviewVariantId(null)}
                            className={cn(
                              "rounded-xl border px-3 py-2 text-sm font-['Rubik'] transition-colors",
                              !previewVariantId ? 'border-primary bg-primary/10 text-primary' : 'border-white/20 text-foreground hover:border-white/40'
                            )}
                          >
                            Galerie produit
                          </button>
                          {previewVariants.map((v) => (
                            <button
                              key={v.id}
                              type="button"
                              onClick={() => setPreviewVariantId(v.id)}
                              className={cn(
                                "rounded-xl border px-3 py-2 text-sm font-['Rubik'] transition-colors",
                                previewVariantId === v.id ? 'border-primary bg-primary/10 text-primary' : 'border-white/20 text-foreground hover:border-white/40'
                              )}
                            >
                              {v.title}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation suppression (un produit) */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setDeleteConfirm(null)}>
          <div
            className={cn('w-full max-w-sm rounded-2xl border p-6 shadow-xl', borderCls, isLight ? 'bg-card' : 'bg-neutral-900')}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-['Rubik'] text-foreground">
              Supprimer « {deleteConfirm.name} » ? Cette action est irréversible.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setDeleteConfirm(null)}>
                Annuler
              </Button>
              <Button
                size="sm"
                className="rounded-xl bg-red-600 hover:bg-red-700"
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={actionLoading === deleteConfirm.id}
              >
                {actionLoading === deleteConfirm.id ? <Loader2 className="size-4 animate-spin" /> : 'Supprimer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation suppression groupée */}
      {bulkDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setBulkDeleteConfirm(false)}>
          <div
            className={cn('w-full max-w-sm rounded-2xl border p-6 shadow-xl', borderCls, isLight ? 'bg-card' : 'bg-neutral-900')}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-['Rubik'] text-foreground">
              Supprimer {selectedIds.size} produit(s) ? Cette action est irréversible.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setBulkDeleteConfirm(false)}>
                Annuler
              </Button>
              <Button
                size="sm"
                className="rounded-xl bg-red-600 hover:bg-red-700"
                onClick={handleBulkDelete}
                disabled={!!actionLoading}
              >
                {actionLoading === 'bulk-delete' ? <Loader2 className="size-4 animate-spin" /> : 'Supprimer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
